import { useMemo } from "react";
import { BookOpen, Eye, FileText, Pencil, Play, RefreshCw, Square, Trash2, Wand2 } from "lucide-react";
import type { AdminResource } from "../../lib/api";
import { Badge, type BadgeVariant } from "../ui/Badge";
import { IngestedBadge } from "./IngestedBadge";
import { AdminDataTable, type Column, type RowAction } from "./dataTable/AdminDataTable";
import { useListState } from "./dataTable/useListState";

type Props = {
  rows: AdminResource[];
  loading: boolean;
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  ingestedPast: Set<string>;
  onView: (r: AdminResource) => void;
  onEdit: (r: AdminResource) => void;
  onDelete: (r: AdminResource) => void;
  onStop: (r: AdminResource) => void;
  onContinue: (r: AdminResource) => void;
  onRetry: (r: AdminResource) => void;
  onExtract: (r: AdminResource) => void;
  onReviewDraft: (r: AdminResource) => void;
  onUpload: () => void;
};

function statusOf(r: AdminResource): { label: string; variant: BadgeVariant } {
  if (r.resourceType === "module") {
    switch (r.extractionStatus) {
      case "queued":
      case "running": return { label: "Extracting", variant: "info" };
      case "extracted": return { label: "Needs review", variant: "warning" };
      case "published": return { label: "Published", variant: "success" };
      case "rejected": return { label: "Rejected", variant: "destructive" };
      case "failed": return { label: "Failed", variant: "destructive" };
      default: return { label: "Not extracted", variant: "secondary" };
    }
  }
  switch (r.ingestionStatus) {
    case "pending":
    case "processing": return { label: "Processing", variant: "info" };
    case "failed": return { label: "Failed", variant: "destructive" };
    case "cancelled": return { label: "Stopped", variant: "secondary" };
    case "completed":
      if (r.resourceType === "previous_exam") return r.isPublished ? { label: "Published", variant: "success" } : { label: "Needs publish", variant: "warning" };
      return { label: "Ready", variant: "success" };
    default: return { label: r.ingestionStatus || "Unknown", variant: "secondary" };
  }
}

/** Modern resources list: one primary action per row, everything else in the "..." menu. */
export function ResourcesList(p: Props) {
  const list = useListState("r", [], { key: "date", dir: "desc" });
  const columns = useMemo<Column<AdminResource>[]>(
    () => [
      { key: "status", header: "Status", className: "w-32 shrink-0", sortValue: (r) => statusOf(r).label, cell: (r) => {
        const s = statusOf(r);
        const err = r.resourceType === "module" ? r.extractionError : r.ingestionError;
        return (
          <span className="flex min-w-0 flex-col gap-0.5">
            <Badge variant={s.variant}>{s.label}</Badge>
            {err && s.variant === "destructive" && <span className="truncate text-xs text-destructive" title={err}>{err}</span>}
          </span>
        );
      } },
      { key: "access", header: "Access", className: "w-24 shrink-0", desktopOnly: true, sortValue: (r) => r.access, cell: (r) => <Badge variant={r.access === "Premium" ? "warning" : "info"}>{r.access}</Badge> },
      { key: "views", header: "Views", className: "w-20 shrink-0", desktopOnly: true, sortValue: (r) => r.views ?? 0, cell: (r) => <span className="font-semibold">{r.views ?? 0}</span> },
      { key: "date", header: "Uploaded", className: "w-28 shrink-0", desktopOnly: true, sortValue: (r) => r.date, cell: (r) => <span className="text-muted-foreground">{r.date}</span> },
    ],
    [],
  );

  const actions: RowAction<AdminResource>[] = [
    { key: "view", label: "View details", icon: <Eye size={14} />, group: 0, quick: true, onSelect: p.onView },
    { key: "edit", label: "Edit details", icon: <Pencil size={14} />, group: 0, quick: true, onSelect: p.onEdit },
    { key: "review", label: "Review draft", icon: <BookOpen size={14} />, group: 1, onSelect: p.onReviewDraft, hidden: (r) => r.resourceType !== "module" || !["extracted", "published", "rejected"].includes(r.extractionStatus ?? "") },
    { key: "extract", label: "Extract content", icon: <Wand2 size={14} />, group: 1, onSelect: p.onExtract, hidden: (r) => r.resourceType !== "module" || !(!r.extractionStatus || r.extractionStatus === "not_requested" || r.extractionStatus === "failed") },
    { key: "continue", label: "Continue processing", icon: <Play size={14} />, group: 1, onSelect: p.onContinue, hidden: (r) => r.resourceType === "module" || !r.canContinue },
    { key: "retry", label: "Retry processing", icon: <RefreshCw size={14} />, group: 1, onSelect: p.onRetry, hidden: (r) => r.resourceType === "module" || !r.canReingest },
    { key: "stop", label: "Stop processing", icon: <Square size={14} />, group: 1, onSelect: p.onStop, hidden: (r) => r.resourceType === "module" || !r.canStop },
    { key: "delete", label: "Delete", icon: <Trash2 size={14} />, group: 2, danger: true, onSelect: p.onDelete },
  ];

  return (
    <AdminDataTable<AdminResource>
      rows={p.rows}
      rowId={(r) => r.id}
      noun="resources"
      loading={p.loading}
      columns={columns}
      actions={actions}
      list={list}
      selected={p.selected}
      onSelectedChange={p.onSelectedChange}
      onOpen={p.onView}
      pageSize={30}
      primary={(r) => {
        if (r.resourceType === "module") {
          if (!r.extractionStatus || r.extractionStatus === "not_requested") return { label: "Extract", onClick: () => p.onExtract(r) };
          if (r.extractionStatus === "failed") return { label: "Retry", onClick: () => p.onExtract(r) };
          if (["extracted", "published", "rejected"].includes(r.extractionStatus)) return { label: "Review", onClick: () => p.onReviewDraft(r) };
          return null;
        }
        if (r.canContinue) return { label: "Continue", onClick: () => p.onContinue(r) };
        if (r.canReingest) return { label: "Retry", onClick: () => p.onRetry(r) };
        return null;
      }}
      titleCell={(r) => (
        <>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
            <FileText size={16} />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <button type="button" onClick={() => p.onView(r)} className="truncate text-left text-sm font-semibold text-foreground hover:text-primary" title={r.title}>
                {r.title}
              </button>
              {r.resourceType === "previous_exam" && p.ingestedPast.has(r.id) && <IngestedBadge />}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {[r.type, r.subject, r.streamName ?? "Common"].filter(Boolean).join(" · ")}
            </span>
          </span>
        </>
      )}
      empty={{ title: "No resources yet", message: "Upload your first module, exam or teacher resource.", action: { label: "Upload resource", onClick: p.onUpload } }}
    />
  );
}
